import React from "react";
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer } from "@react-google-maps/api";
import { QRCodeSVG } from "qrcode.react";
import { useGoogleMap, PlaceState, PlaceDetail } from "./hooks/useGoogleMap";

// GoogleMapComponentのプロパティ型
interface GoogleMapProps {
  travelMode?: "WALKING" | "DRIVING" | "BICYCLING" | "TRANSIT";
}

// 施設情報ウィンドウのプロパティ型
type PlaceInfoWindowProps = {
  selectedPlaceDetail: PlaceDetail | null;
  clickedPosition: google.maps.LatLngLiteral | null;
  placeDurations: { [mode: string]: string } | null;
  t: (k: string) => string;
};

const MAP_CONTAINER_STYLE = { width: "100%", height: "600px" };

/**
 * 施設情報ウィンドウ
 *
 * @param selectedPlaceDetail 施設の詳細情報
 * @param clickedPosition クリックした位置情報
 * @param placeDurations 移動手段ごとの所要時間
 * @param t 翻訳関数
 * @returns React.JSX.Element
 */
const PlaceInfoWindow: React.FC<PlaceInfoWindowProps & { onClose: () => void }> = ({
  selectedPlaceDetail,
  clickedPosition,
  placeDurations,
  t,
  onClose,
}) => {
  if (!selectedPlaceDetail || !clickedPosition) return null;
  return (
    <InfoWindow position={clickedPosition} onCloseClick={onClose}>
      <div className="google-map-info-window">
        <div className="info-header">
          <strong>{selectedPlaceDetail.name}</strong>
          <hr />
        </div>
        <div className="info-body">
          {selectedPlaceDetail.website && (
            <>
              <span className="label">
                <span className="material-symbols-outlined">qr_code</span>
                {t("officialWebsite")}
              </span>
              <QRCodeSVG value={selectedPlaceDetail.website} size={88} />
              <hr />
            </>
          )}

          {placeDurations && (
            <>
              <span className="label">
                <span className="material-symbols-outlined">directions</span>
                {t("travelTime")}
              </span>
              <ul className={"contents"}>
                {Object.entries(placeDurations).map(([mode, time]) => (
                  <li key={mode}>
                    {mode === "WALKING" && (
                      <>
                        <span className="material-symbols-outlined">directions_walk</span>
                        {t("WALKING")}
                      </>
                    )}
                    {mode === "DRIVING" && (
                      <>
                        <span className="material-symbols-outlined">directions_car</span>
                        {t("DRIVING")}
                      </>
                    )}
                    {mode === "BICYCLING" && (
                      <>
                        <span className="material-symbols-outlined">directions_bike</span>
                        {t("BICYCLING")}
                      </>
                    )}
                    {mode === "TRANSIT" && (
                      <>
                        <span className="material-symbols-outlined">directions_transit</span>
                        {t("TRANSIT")}
                      </>
                    )}
                    : {time}
                  </li>
                ))}
              </ul>
              <hr />
            </>
          )}

          {selectedPlaceDetail.address && (
            <>
              <span className="label">
                <span className={"material-symbols-outlined"}>location_on</span>
                {t("address")}
              </span>
              <div className="contents">{selectedPlaceDetail.address}</div>
              <hr />
            </>
          )}

          {selectedPlaceDetail.phone && (
            <>
              <br />
              <span className="label">
                <span className="material-symbols-outlined">call</span>
                {t("phone")}
              </span>
              {selectedPlaceDetail.phone}
              <hr />
            </>
          )}

          {selectedPlaceDetail.opening_hours && selectedPlaceDetail.opening_hours.length > 0 && (
            <>
              <br />
              <span className="label">
                <span className="material-symbols-outlined">schedule</span>
                {t("businessHours")}
              </span>
              <ul>
                {selectedPlaceDetail.opening_hours.map((txt, i) => (
                  <li key={i}>{txt}</li>
                ))}
              </ul>
              <hr />
            </>
          )}
        </div>
      </div>
    </InfoWindow>
  );
};

/**
 * 検索結果のリストを表示するコンポーネント
 *
 * @param results 検索結果の配列
 * @param selectedIdx 選択中のインデックス
 * @param onClick クリック時のコールバック関数
 * @returns React.JSX.Element
 */
const ResultsList: React.FC<{
  results: PlaceState[];
  selectedIdx?: number | null;
  onClick: (i: number) => void;
}> = ({ results, selectedIdx, onClick }) => (
  <div className={"google-map-results"}>
    <ul>
      {results.map((place, index) => (
        <React.Fragment key={index}>
          <li
            className={selectedIdx === index ? "highlighted" : ""}
            style={{ cursor: "pointer" }}
            onClick={() => onClick(index)}
          >
            {index + 1}. {place.name} - {place.distance}m
          </li>
          <hr />
        </React.Fragment>
      ))}
    </ul>
  </div>
);

/**
 * GoogleMapsを表示し、検索結果をリスト化するコンポーネント
 *
 * @param travelMode "DRIVING" | "WALKING" | "BICYCLING" | "TRANSIT"
 * @returns React.JSX.Element
 */
const GoogleMapComponent: React.FC<GoogleMapProps> = ({ travelMode }) => {
  const {
    apiKey,
    results,
    center,
    selectedIdx,
    clickedPosition,
    selectedPlaceDetail,
    directions,
    placeDurations,
    fetchPlaceDetail,
    handleMapClick,
    t,
    handleCloseInfoWindow,
  } = useGoogleMap(travelMode);

  return (
    <div className="google-map-search-container">
      <div className={"google-map-container"}>
        {apiKey && (
          <GoogleMap
            center={center}
            zoom={15}
            mapContainerStyle={MAP_CONTAINER_STYLE}
            onClick={handleMapClick}
            options={{
              fullscreenControl: false,
              streetViewControl: false,
              mapTypeControl: true,
              zoomControl: true,
              scaleControl: true,
              rotateControl: false,
              keyboardShortcuts: false,
              disableDefaultUI: false,
            }}
          >
            <Marker position={center} label="center" />
            {results.map((place, idx) => (
              <Marker
                position={place.position}
                key={idx}
                onClick={() => fetchPlaceDetail(idx)}
                label={`${idx + 1}`}
              />
            ))}

            {clickedPosition && selectedPlaceDetail && (
              <PlaceInfoWindow
                selectedPlaceDetail={selectedPlaceDetail}
                clickedPosition={clickedPosition}
                placeDurations={placeDurations}
                t={t}
                onClose={handleCloseInfoWindow}
              />
            )}

            {directions && (
              <DirectionsRenderer directions={directions} key={travelMode + "-" + selectedIdx} />
            )}
          </GoogleMap>
        )}
      </div>

      <ResultsList results={results} selectedIdx={selectedIdx} onClick={fetchPlaceDetail} />
    </div>
  );
};

export default GoogleMapComponent;
